import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Users } from 'lucide-react';
import { Avatar } from './Avatar';
import { cn } from '@/lib/utils';

export interface MentionUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

/** Pseudo-user offered in the mention dropdown to notify all project members. */
export const EVERYONE_MENTION_ID = 'everyone';

export const EVERYONE_MENTION: MentionUser = {
  id: EVERYONE_MENTION_ID,
  name: 'Everyone',
  email: 'Notify all project members',
};

interface MentionListProps {
  items: MentionUser[];
  command: (item: { id: string; label: string }) => void;
  isLoading?: boolean;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command, isLoading }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command({ id: item.id, label: item.name });
      }
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    if (isLoading) {
      return (
        <div className="bg-white rounded-lg shadow-elevated border border-grey-200 p-3 text-sm text-grey-500">
          Loading...
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-elevated border border-grey-200 p-3 text-sm text-grey-500">
          No users found
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-elevated border border-grey-200 overflow-hidden max-h-64 overflow-y-auto">
        {items.map((item, index) => (
          <button
            key={item.id}
            onClick={() => selectItem(index)}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 text-left transition-colors',
              index === selectedIndex
                ? 'bg-primary/10 text-primary'
                : 'hover:bg-grey-50'
            )}
          >
            {item.id === EVERYONE_MENTION_ID ? (
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 shrink-0">
                <Users className="w-4 h-4 text-primary" />
              </span>
            ) : (
              <Avatar
                src={item.avatarUrl}
                name={item.name}
                userId={item.id}
                size="sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{item.name}</p>
              <p className="text-xs text-grey-500 truncate">{item.email}</p>
            </div>
          </button>
        ))}
      </div>
    );
  }
);

MentionList.displayName = 'MentionList';


